<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkRentalServiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    items: {
      type: Array,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromInput = (...args) => {
    const [inputItems, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    _.each(inputItems, (item, i) => {
      if (i === 0) {
        result = _.pick(item, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != item[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = item[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});
    return result;
  };
  const getPayload = (...args) => {
    const [inputItems, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(inputItems, (item) => ({
        id: item.id,
        etag: item.etag,
        value: update,
      })),
    };
  };

  const editableFields = ref([
    {
      name: 'service',
    },
    {
      name: 'name',
    },
    {
      name: 'billing_unit',
    },
    {
      name: 'invoice_notes',
    },
    {
      name: 'invoice_notes',
    },
    {
      name: 'valid_from',
    },
    {
      name: 'valid_to',
    },
    {
      name: 'tariff_code',
    },
    {
      name: 'meter_factor',
    },
    {
      name: 'intermediated_service',
    },
    {
      name: 'inflation_indexed',
    },
    {
      name: 'notes',
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BulkRentalServiceEditor');
  const item = ref(null);
  const services = ref(null);
  const tariffs = ref(null);
  const units = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_3468003412020058_dataSource = computed(() => ({
    name: 'suggest_product',
    parameters: {
      product_types: 'service',
    },
  }));

  const $data = computed(() => ({
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    services: services.value,
    tariffs: tariffs.value,
    units: units.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3468003412020058_dataSource: b_3468003412020058_dataSource.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return getItemFromInput(props.items, $data.value.editableFields);
  };
  const $$load_services = async () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_tariffs = async () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };
  const $$load_units = async () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };
  const $$load_invoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'editableFields' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'services' },
      { name: 'tariffs' },
      { name: 'units' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3468003412020058_dataSource', nodeId: '3468003412020058' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      services: $$load_services,
      tariffs: $$load_tariffs,
      units: $$load_units,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      services,
      tariffs,
      units,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3468003412020058_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkRentalServiceEditor',
        'Edit Service Properties'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_go5srz2h0_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_1ukumt5mb_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_s1qg0wur2_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_rental_service',
      data: getPayload(props.items, item.value),
    };
  };
  const act_3468003412020058_setData_5bbqteosw_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: item.value.service.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_setData_oik72r4c8_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_go5srz2h0 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_go5srz2h0_getActionArgs,
    });
  const inst_evh_7315092382398562_close_2rfnnc34f =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_go5srz2h0,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_2rfnnc34f],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_1c9fzzfvy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8asjtsyld =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1c9fzzfvy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8asjtsyld],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_c81r0qwxg =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_46f9iknyj =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_c81r0qwxg,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_46f9iknyj],
    event: 'close',
  });
  const act_1040167445267876_closeModal_1ukumt5mb =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_1ukumt5mb_getActionArgs,
    });
  const inst_evh_1040167445267876_success_crtn7hd2z =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_1ukumt5mb,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_s1qg0wur2 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_s1qg0wur2_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_crtn7hd2z],
  });
  const inst_evh_1040167445267876_submit_rzmst0nag =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_s1qg0wur2,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_rzmst0nag],
    event: 'submit',
  });
  const act_3468003412020058_setData_5bbqteosw = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_5bbqteosw_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_l9h62imty =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_5bbqteosw,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3468003412020058_setData_oik72r4c8 = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_oik72r4c8_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_io7x8u8rq =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_oik72r4c8,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_3468003412020058_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_3468003412020058_optionSelected_l9h62imty,
        inst_evh_3468003412020058_optionSelected_io7x8u8rq,
      ],
      event: 'optionSelected',
    });
  const act_2434514588044777_closeComponent_biqjrjc2o =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_cbcqylhx8 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_biqjrjc2o,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_cbcqylhx8],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_2rfnnc34f,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_8asjtsyld,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_46f9iknyj,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_crtn7hd2z,
    inst_evh_1040167445267876_submit_rzmst0nag,
    save: inst_evh_1040167445267876_submit_rzmst0nag,
    inst_evh_1040167445267876_submit,
    inst_evh_3468003412020058_optionSelected_l9h62imty,
    inst_evh_3468003412020058_optionSelected_io7x8u8rq,
    inst_evh_3468003412020058_optionSelected,
    inst_evh_2434514588044777_clickPreventStop_cbcqylhx8,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_2rfnnc34f = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_2rfnnc34f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_8asjtsyld = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8asjtsyld.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_46f9iknyj = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_46f9iknyj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_crtn7hd2z = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_crtn7hd2z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_rzmst0nag = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_rzmst0nag.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_l9h62imty = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_l9h62imty.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_io7x8u8rq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_io7x8u8rq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3468003412020058_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_cbcqylhx8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_cbcqylhx8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkRentalServiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext('BulkRentalServiceEditor|ResourceTitle', 'Rental Services')
        "
        :resourceKey="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="service"
          :label="pgettext('RentalService', 'Service')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="service_enabled"
                v-model="item.$updates.service"
                :value="item.$updates.service"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.service =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSelect2
            name="service"
            v-model="item.service"
            :value="item.service"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.service =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :dataSource="b_3468003412020058_dataSource"
            bind-object
            valueField="id"
            textField="name"
            :disabled="!item.$updates.service"
            required
            @optionSelected="evh_3468003412020058_optionSelected($event, {})"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="gettext('Valid From')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_from_enabled"
                v-model="item.$updates.valid_from"
                :value="item.$updates.valid_from"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.valid_from =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_from"
            v-model="item.valid_from"
            :disabled="!item.$updates.valid_from"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_to"
          :label="gettext('Valid To')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_to_enabled"
                v-model="item.$updates.valid_to"
                :value="item.$updates.valid_to"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.valid_to =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_to"
            v-model="item.valid_to"
            :disabled="!item.$updates.valid_to"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="intermediated_service"
          :label="gettext('Intermediated service')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="intermediated_service_enabled"
                v-model="item.$updates.intermediated_service"
                :value="item.$updates.intermediated_service"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.intermediated_service =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormCheckbox
            type="number"
            name="intermediated_service"
            v-model="item.intermediated_service"
            :value="item.intermediated_service"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.intermediated_service =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.intermediated_service"
            class="w-auto"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="inflation_indexed"
          :label="gettext('Inflation indexed')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="inflation_indexed_enabled"
                v-model="item.$updates.inflation_indexed"
                :value="item.$updates.inflation_indexed"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.inflation_indexed =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormCheckbox
            type="number"
            name="inflation_indexed"
            v-model="item.inflation_indexed"
            :value="item.inflation_indexed"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.inflation_indexed =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.inflation_indexed"
            class="w-auto"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff_code"
          :label="pgettext('RentalService', 'Tariff')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          v-if="tariffs && tariffs.length"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="tariff_code_enabled"
                v-model="item.$updates.tariff_code"
                :value="item.$updates.tariff_code"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.tariff_code =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSelect2
            name="tariff_code"
            v-model="item.tariff_code"
            :value="item.tariff_code"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.tariff_code =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="tariffs"
            clearable
            valueField="code"
            textField="code,name"
            :disabled="!item.$updates.tariff_code"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_unit"
          :label="pgettext('RentalService', 'Billing unit')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="billing_unit_enabled"
                v-model="item.$updates.billing_unit"
                :value="item.$updates.billing_unit"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.billing_unit =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSelect2
            name="billing_unit"
            v-model="item.billing_unit"
            :value="item.billing_unit"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.billing_unit =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="units"
            valueField="symbol"
            textField="symbol"
            :disabled="!item.$updates.billing_unit"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('RentalService', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="notes_enabled"
                v-model="item.$updates.notes"
                :value="item.$updates.notes"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.$updates.notes =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :value="item.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
